import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Row, Col, notification, PageHeader, UploadProps, UploadFile } from 'antd';
import { ArrowLeftOutlined, ImportOutlined } from '@ant-design/icons';
import querystring from 'querystring';

import { StatusType } from 'App/types/requestStatus';
import { useTranslation } from 'react-i18next';
import { RootState } from 'App/globalState/root.reducer';
import { adminCreateEvent, getAdminEvent } from 'App/globalState/events/events.global.thunk';
import { cleanUpAdminEvent, cleanUpEventsStatus } from 'App/globalState/events/events.global.slice';
import CreateEventForm from './forms/createEventForm/CreateEventForm';
import { AdminCreateEventRequest } from 'App/api/endpoints/events/requests/adminCreateEventRequest';
import { URLUtils } from 'App/pages/homePages/views/home/utils/URLUtils';
import { ProxyEventImportModal } from './utils/ProxyEventImportModal';

const { LOADING } = StatusType;

export const CreateEventContainer = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const { t } = useTranslation(['page', 'common']);

	const eventsStatus = useSelector((state: RootState) => state.global.events.status);
	const duplicateEvent = useSelector((state: RootState) => state.global.events.adminEvent);

    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [bannerFileBuffer, setBannerFileBuffer] = useState<ArrayBuffer | null>(null);
    
	const [posterFile, setPosterFile] = useState<File | null>(null);
    const [posterFileBuffer, setPosterFileBuffer] = useState<ArrayBuffer | null>(null);
	
	const [initialValues, setInitialValues] = useState(null);
	const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);

    const isFileImage = (file: File): boolean => {
        const isImage = file.type === 'image/png'
					 || file.type === 'image/jpeg'
					 || file.type === 'image/webp';
        
        if (!isImage) {
            notification.error({
				message: t('common:Errors.Error'),
				description: t('AdminPages.EventsPages.FileIsNotImage'),
			});
        }

        return isImage;
    }

	const fileToUl = (file: File) => {
		return [{
			uid: file.name,
			name: file.name,
			status: 'done',
			url: URL.createObjectURL(file),
		} as UploadFile]
	}

    const uploadBannerProps: UploadProps = {
        name: 'banner',
        multiple: false,
        maxCount: 1,
        showUploadList: bannerFile != null,
        accept: "image/png, image/jpeg, image/webp",
		fileList: bannerFile ? fileToUl(bannerFile) : [],
        onRemove: (file) => {
            setBannerFile(null);
		},
		beforeUpload: (file : File) => {
			const fileSizeInMb = file.size / 1024 / 1024;
			if(fileSizeInMb >= 1){
				notification.error({
					message: t('common:Errors.Error'),
					description: t('AdminPages.EventsPages.FileIsTooLarge'),
				});
				return false;
			}

            if(isFileImage(file)) {
                setBannerFile(file);
				file.arrayBuffer().then(buf => setBannerFileBuffer(buf));
			}
            else
                setBannerFile(null);

			return false;
		}
    };

    const uploadPosterProps: UploadProps = {
        name: 'poster',
        multiple: false,
        maxCount: 1,
        showUploadList: bannerFile != null,
        accept: "image/png, image/jpeg, image/webp",
		fileList: posterFile ? fileToUl(posterFile) : [],
        onRemove: (file) => {
            setPosterFile(null);
		},
		beforeUpload: (file : File) => {
			const fileSizeInMb = file.size / 1024 / 1024;
			if(fileSizeInMb >= 1){
				notification.error({
					message: t('common:Errors.Error'),
					description: t('AdminPages.EventsPages.FileIsTooLarge'),
				});
				return false;
			}
			
            if(isFileImage(file)) {
                setPosterFile(file);
				file.arrayBuffer().then(buf => setPosterFileBuffer(buf));
			}
            else
                setPosterFile(null);

			return false;
		}
    };
      
	const handleFormSubmit = (values: AdminCreateEventRequest) => {
		if (bannerFile)
        	values.banner = new Blob([bannerFileBuffer], { type: bannerFile!.type });
		if (posterFile)
        	values.poster = new Blob([posterFileBuffer], { type: posterFile!.type });

		const onSuccess = () => {
			notification.success({
				message: t('common:Success.Success'),
				description: t('AdminPages.EventsPages.SuccessCreateDescription'),
			});

            history.push('/admin/events');
		};
		dispatch(adminCreateEvent(values, onSuccess));
	};

	const handleImport = async (data: any) => {
		console.log("Import", data);
		setIsImportModalOpen(false);

        if (data !== null) {
            setInitialValues({
			    ...data
		    });
        }		
	}

	const handleImportCancel = () => {
		setIsImportModalOpen(false);
	}

	useEffect(() => {
		return () => {
			dispatch(cleanUpEventsStatus());
			dispatch(cleanUpAdminEvent());
		};
	}, [dispatch]);

	useEffect(()=> {
		if (location.search) {
			const search = querystring.parse(location.search.substring(1));

			if(search.duplicateid) {
				const duplicateId = URLUtils.isArrayOfStrings(search.duplicateid) ? search.duplicateid[0] : search.duplicateid as string;
				dispatch(getAdminEvent(duplicateId));
			}
		} else {
			dispatch(cleanUpAdminEvent());
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location,]);

	useEffect(() => {
		if(duplicateEvent) {
			console.warn("NEW DUPLICATE", duplicateEvent);
			setInitialValues({
				...duplicateEvent,
				banner: bannerFile,
				poster: posterFile
			} as AdminCreateEventRequest);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [duplicateEvent])

	return (
		<React.Fragment>
			<ProxyEventImportModal
				open={isImportModalOpen} 
				onOk={handleImport} 
				onCancel={handleImportCancel}
			/>
			<Row className='mb-5'>
				<Col>
					<Button
						style={{ marginLeft: 16 }}
						onClick={() => history.push('/admin/events')}
						icon={<ArrowLeftOutlined />}
					>
						{t('common:Actions.GoBack')}
					</Button>
				</Col>
				<Col style={{ marginLeft: 16 }}>
					<Button
						onClick={() => setIsImportModalOpen(true)}
						icon={<ImportOutlined />}
					>
						{t('common:Actions.Import')}
					</Button>
				</Col>
			</Row>
			<Row justify='center'>
				<Col span={24}>
					<Row justify='center'>
						<Col>
							<PageHeader title={t('AdminPages.EventsPages.CreatePageHeaderTitle')} />
						</Col>
					</Row>
					<Row justify='center'>
						<Col xs={24} md={20} lg={15} xl={10} xxl={8}>
							<CreateEventForm
                                loading={eventsStatus.adminCreateEvent === LOADING}
                                onFinish={handleFormSubmit}
								initialValues={initialValues}
                                bannerUploadProps={uploadBannerProps}
                                posterUploadProps={uploadPosterProps}
                            />
						</Col>
					</Row>
				</Col>
			</Row>
		</React.Fragment>
	);
};

export default CreateEventContainer;
